import React, { useEffect, useCallback, useRef, useState } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import {
  Button,
  Box,
  Autocomplete,
  TextField,
  Checkbox,
  FormControlLabel,
  Paper,
  Divider,
  Alert,
} from '@mui/material';
import { setData, valdiateStep1 } from 'reducer/trainingSlice';
import { useSelector, useDispatch } from 'react-redux';

import {
  fetchRegions,
  fetchBatchData,
  fetchDailyEmployeesWhoJoined,
  fetchRegionsBranches,
} from 'thunks/trainingThunks/thunk';
import { useLocation } from 'react-router-dom';
import TrainingLoader from 'Component/TrainingLoader';
import { fetchAllTrainingTypes } from 'thunks/trainingThunks/thunk';
import useIsLearningSuperUser from 'hooks/useIsLearningSuperUser';
import useIsLearningTrainer from 'hooks/useIsLearningTrainer';
import useIsLearningManager from 'hooks/useIsLearningManager';
export const batchFormSteps = ['Batch Info', 'Select Trainees'];

const BatchInfoForm = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const renderCount = useRef(0);
  const [regionIds, setRegionIds] = useState([]);
  const [selectAllRegions, setSelectAllRegions] = useState(false);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  renderCount.current += 1;
  const queryParams = new URLSearchParams(location.search);
  const [selectAll, setSelectAll] = useState(false);
  const [formValues, setFormValues] = useState(null);
  const isLearningSuperUser = useIsLearningSuperUser();
  const isLearningManager = useIsLearningManager();
  const isLearningTrainer = useIsLearningTrainer();
  const batchValue = queryParams.get('batch');
  const trainingFormDetail = useSelector((state) => state?.trainingSlice);
  console.log('isLearningSuperUser', isLearningSuperUser);
  const { trainingTypes } = useSelector((state) => state?.trainingTypePageSlice.data);
  const { availableBranches } = useSelector((state) => state?.trainingSlicePrefetchData);
  const memoizedFetchRegions = useCallback(() => {
    dispatch(fetchRegions());
    dispatch(fetchAllTrainingTypes());
  }, [dispatch]);

  useEffect(() => {
    if (batchValue) {
      const fetchData = async () => {
        dispatch(fetchBatchData({ dispatch, batchId: batchValue }));
        memoizedFetchRegions();
      };
      fetchData();
    } else {
      memoizedFetchRegions();
    }
  }, [memoizedFetchRegions, batchValue]);
  useEffect(() => {
    if (regionIds.length > 0) {
      dispatch(fetchRegionsBranches({ regions: regionIds }));
    }
  }, [regionIds]);
  const { batchType, duration, loading, isApproved } = trainingFormDetail;
  //console.log("renderCount", renderCount.current, loading);
  console.log('trainingFormDetail', trainingFormDetail);

  useEffect(() => {
    if (isLoadingBranches) {
      // Simulate branch loading time or wait for actual API call
      const timer = setTimeout(() => {
        setIsLoadingBranches(false);
      }, 1000); // Adjust this timeout based on your actual API call time
      return () => clearTimeout(timer);
    }
  }, [isLoadingBranches]);

  const handleDurationChange = (selectedDuration, setFieldValue) => {
    const days = parseInt(selectedDuration.split('-')[0], 10) || 1;
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    const newDates = Array.from({ length: days }, (_, index) => {
      const date = new Date(tomorrow);
      date.setDate(tomorrow.getDate() + index);
      return {
        day: `Day ${index + 1}`,
        date: date.toISOString().split('T')[0] + 'T09:00',
      };
    });
    const newEndDates = Array.from({ length: days }, () => ({ date: '18:30' }));
    setFieldValue('dates', newDates);
    setFieldValue('datesEnd', newEndDates);
  };

  const initialValues = {
    batchName: trainingFormDetail.batchName ?? '',
    batchType: trainingFormDetail.batchType ?? 'offline',
    description: trainingFormDetail.description ?? '',
    duration: trainingFormDetail.duration ?? 'Select Duration',
    address: trainingFormDetail.address ?? '',
    googleMapsLink: trainingFormDetail.googleMapsLink ?? '',
    trainingType: trainingFormDetail.trainingType ?? '',
    dates: trainingFormDetail.dates ?? [
      {
        day: 'Day 1',
        date: (() => {
          const tomorrow = new Date();
          tomorrow.setDate(tomorrow.getDate() + 1);
          return tomorrow.toISOString().split('T')[0] + 'T09:00';
        })(),
      },
    ],
    datesEnd: trainingFormDetail.datesEnd ?? [{ date: '18:30' }],
    locationVenue: trainingFormDetail.locationVenue ?? '',
    trainingLink: trainingFormDetail.trainingLink ?? '',
    region: trainingFormDetail?.region ?? [],
    branch: trainingFormDetail?.branch ?? [],
    trainingRoLocation: trainingFormDetail?.trainingRoLocation?.label ?? '',
  };

  const validationSchema = Yup.object({
    trainingType: Yup.string().required('Training Type is required'),
    batchType: Yup.string().required('Batch type is required'),
    duration: Yup.string().required('Duration is required'),
    description: Yup.string(),
    address: Yup.string().when('batchType', (batchType, schema) => {
      return Array.isArray(batchType) && batchType.includes('offline')
        ? schema.required('Address is required')
        : schema.optional();
    }),
    googleMapsLink: Yup.string().when('batchType', (batchType, schema) => {
      return Array.isArray(batchType) && batchType.includes('offline')
        ? schema.url('Must be a valid URL').required('Google Maps link is required')
        : schema.optional();
    }),
    trainingLink: Yup.string()
      .url('Must be a valid URL')
      .when('batchType', (batchType, schema) => {
        return Array.isArray(batchType) && batchType.includes('online')
          ? schema.required('Link is required')
          : schema.optional();
      }),
    dates: Yup.array()
      .of(
        Yup.object({
          day: Yup.string().required('Day is required'),
          date: Yup.date()
            .required('Start time is required')
            .when([], {
              is: () => !isLearningSuperUser && !isLearningManager,
              then: (schema) =>
                schema
                  .min(new Date(), 'Date cannot be in the past')
                  .test(
                    'is-sequential',
                    'Each date must be greater than or equal to the previous date',
                    function (value, context) {
                      const { path, parent, options } = context;
                      const index = path.match(/\d+/)?.[0]; // Extract array index
                      if (index && index > 0) {
                        const previousDate = options.context?.dates?.[index - 1]?.date;
                        return !previousDate || new Date(value) >= new Date(previousDate);
                      }
                      return true;
                    }
                  ),
              otherwise: (schema) => schema,
            }),
        })
      )
      .required('Dates are required')
      .min(1, 'At least one date is required'),
    datesEnd: Yup.array()
      .of(
        Yup.object({
          date: Yup.string()
            .required('End time is required')
            .when([], {
              is: () => !isLearningSuperUser && !isLearningManager,
              then: (schema) =>
                schema.test(
                  'is-after-start-time',
                  'End time must be after start time',
                  function (value) {
                    const { path, options } = this;
                    const index = path.match(/\d+/)?.[0];
                    const startDate = options.context?.dates?.[index]?.date;
                    const endTimeValue = options?.context?.datesEnd[index].date;

                    if (!startDate || !value) return true;

                    const startTime = new Date(startDate);
                    const endTime = new Date(`${startDate.split('T')[0]}T${endTimeValue}`);
                    return endTime > startTime;
                  }
                ),
              otherwise: (schema) => schema,
            }),
        })
      )
      .required('End times are required'),
    region: Yup.array().min(1, 'At least one region is required').required('Region is required'),
    branch: Yup.array().min(1, 'At least one branch is required').required('branch is required'),
    trainingRoLocation: Yup.string().when('batchType', (batchType, schema) => {
      return Array.isArray(batchType) && batchType.includes('offline')
        ? schema.required('Training RO Location is required')
        : schema.optional();
    }),
  });

  const handleFormSubmit = async (values) => {
    const payload = {
      ...values,
    };

    console.log('Form Data', payload);
    dispatch(setData(payload));
    dispatch(valdiateStep1());
    await dispatch(
      fetchDailyEmployeesWhoJoined({
        region: values.region,
        branches: values.branch,
        trainingType: values.trainingType,
        batchId: batchValue ?? null,
        trainingRoLocation: values.trainingRoLocation,
      })
    );
  };

  const onSubmit = async (values) => {
    handleFormSubmit(values);
  };

  if (loading === undefined || loading === true) {
    return <TrainingLoader />;
  }

  const handleToggleSelectAll = (setFieldValue) => {
    if (selectAll) {
      setFieldValue('branch', []); // Deselect all
    } else {
      setFieldValue('branch', availableBranches); // Select all
    }
    setSelectAll(!selectAll);
  };

  const handleToggleSelectAllRegions = (setFieldValue) => {
    const allRegions = trainingFormDetail?.availableRegions || [];
    const newValue = !selectAllRegions ? allRegions : [];
    setFieldValue('region', newValue);
    setSelectAllRegions(!selectAllRegions);

    if (!selectAllRegions) {
      // When selecting all, start loading branches
      setIsLoadingBranches(true);
      let tempValues = [...allRegions];
      let regionIds = tempValues.map((item) => item.id);
      setRegionIds([...regionIds]);
    } else {
      // When deselecting all, reset branches
      setFieldValue('branch', []);
      setRegionIds([]);
    }
  };

  return (
    <Box
      sx={{
        margin: '0 auto',
        padding: '20px',
        borderRadius: '8px',
        background: '#fff',
      }}
    >
      {isApproved && !isLearningSuperUser && !isLearningManager && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Batch is already approved, editing is not allowed
        </Alert>
      )}
      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={onSubmit}
        enableReinitialize={true}
      >
        {({ isSubmitting, values, setFieldValue, errors }) => (
          <Form
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'start',
            }}
          >
            <Box sx={{ marginBottom: '16px', width: '300px', margin: '5px' }}>
              <label htmlFor="trainingType">Training Type</label>
              <Field
                as="select"
                id="trainingType"
                name="trainingType"
                disabled={isApproved && !isLearningSuperUser && !isLearningManager}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px',
                  marginTop: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                }}
              >
                <option value="" label="Select Training Type" />
                {/* {console.log("trainingTypes", trainingTypes)} */}
                {trainingTypes.map((elem, idx) => {
                  return (
                    <option
                      key={elem.trainingName}
                      value={elem.trainingName}
                      label={elem.trainingName}
                    />
                  );
                })}
              </Field>
              <ErrorMessage
                name="trainingType"
                component="div"
                style={{ color: 'red', fontSize: '0.9em', marginTop: '4px' }}
              />
            </Box>
            <Box sx={{ marginBottom: '16px', width: '300px', margin: '5px' }}>
              <label htmlFor="region">Region</label>
              <Field name="region">
                {({ field, form }) => {
                  const selectedRegions = trainingFormDetail?.region;
                  return (
                    <Autocomplete
                      multiple
                      id="region"
                      disabled={isApproved && !isLearningSuperUser && !isLearningManager}
                      defaultValue={selectedRegions}
                      options={
                        trainingFormDetail?.availableRegions ?? [
                          { id: '1', label: 'Region 1' },
                          { id: '2', label: 'Region 2' },
                          { id: '3', label: 'Region 3' },
                        ]
                      }
                      getOptionLabel={(option) => option.label || ''}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      onChange={(event, value) => {
                        form.setFieldValue('region', value);
                        setSelectAllRegions(
                          value.length === (trainingFormDetail?.availableRegions?.length || 0)
                        );
                        if (value.length === 0) {
                          form.setFieldValue('branch', []);
                          setRegionIds([]);
                        } else {
                          setIsLoadingBranches(true);
                          let tempValues = [...value];
                          let regionIds = tempValues.map((item) => item.id);
                          setRegionIds([...regionIds]);
                        }
                      }}
                      onBlur={() => {
                        form.setFieldTouched('region', true); // Manually mark the field as touched
                      }}
                      value={form.values.region || []}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder="Select Regions"
                          style={{
                            display: 'block',
                            width: '100%',
                            marginTop: '8px',
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            maxHeight: '80px',
                            overflowY: 'auto',
                            scrollbarWidth: 'none',
                          }}
                        />
                      )}
                      PaperComponent={(paperProps) => {
                        const { children, ...restPaperProps } = paperProps;
                        return (
                          <Paper
                            {...restPaperProps}
                            style={{
                              overflowY: 'auto',
                              scrollbarWidth: 'none',
                            }}
                            sx={{
                              '&::-webkit-scrollbar': {
                                display: 'none',
                              },
                            }}
                          >
                            <Box onMouseDown={(e) => e.preventDefault()} pl={1.5} py={0.5}>
                              <FormControlLabel
                                onClick={(e) => {
                                  e.preventDefault();
                                  handleToggleSelectAllRegions(setFieldValue);
                                }}
                                label="Select All"
                                control={
                                  <Checkbox
                                    id="select-all-regions-checkbox"
                                    checked={selectAllRegions}
                                  />
                                }
                              />
                            </Box>
                            <Divider />
                            {children}
                          </Paper>
                        );
                      }}
                    />
                  );
                }}
              </Field>
              <ErrorMessage
                name="region"
                component="div"
                style={{ color: 'red', fontSize: '0.9em', marginTop: '4px' }}
              />
            </Box>
            <Box sx={{ marginBottom: '16px', width: '300px', margin: '5px' }}>
              <label htmlFor="branch">Branch</label>
              <Field name="branch">
                {({ field, form }) => {
                  const selectedBranches = trainingFormDetail?.branch;

                  return (
                    <Autocomplete
                      multiple
                      disabled={
                        (isApproved && !isLearningSuperUser && !isLearningManager) ||
                        isLoadingBranches ||
                        !form.values.region?.length
                      }
                      id="branch"
                      defaultValue={selectedBranches}
                      options={
                        availableBranches ?? [
                          { id: '1', label: 'Branch 1' },
                          { id: '2', label: 'Branch 2' },
                          { id: '3', label: 'Branch 3' },
                        ]
                      }
                      getOptionLabel={(option) => option.label || ''}
                      isOptionEqualToValue={(option, value) => option.id === value.id}
                      onChange={(event, value) => {
                        form.setFieldValue('branch', value); // Set value directly
                        setSelectAll(value.length === (availableBranches?.length || 0)); // Update "Select All" state
                      }}
                      onBlur={() => {
                        form.setFieldTouched('branch', true); // Manually mark the field as touched
                      }}
                      value={form.values.branch || []}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          variant="standard"
                          placeholder={
                            isLoadingBranches ? 'Loading branches...' : 'Select Branches'
                          }
                          style={{
                            display: 'block',
                            width: '100%',
                            marginTop: '8px',
                            borderRadius: '4px',
                            border: '1px solid #ccc',
                            maxHeight: '80px',
                            overflowY: 'auto',
                            scrollbarWidth: 'none', // Hide scrollbar for Firefox
                          }}
                        />
                      )}
                      PaperComponent={(paperProps) => {
                        const { children, ...restPaperProps } = paperProps;
                        return (
                          <Paper
                            {...restPaperProps}
                            style={{
                              overflowY: 'auto',
                              scrollbarWidth: 'none', // Hide scrollbar for Firefox
                            }}
                            sx={{
                              '&::-webkit-scrollbar': {
                                display: 'none', // Hide scrollbar for WebKit browsers
                              },
                            }}
                          >
                            <Box
                              onMouseDown={(e) => e.preventDefault()} // Prevent blur
                              pl={1.5}
                              py={0.5}
                            >
                              <FormControlLabel
                                onClick={(e) => {
                                  e.preventDefault(); // Prevent blur
                                  handleToggleSelectAll(setFieldValue);
                                }}
                                label="Select All"
                                control={<Checkbox id="select-all-checkbox" checked={selectAll} />}
                              />
                            </Box>
                            <Divider />
                            {children}
                          </Paper>
                        );
                      }}
                    />
                  );
                }}
              </Field>
              <ErrorMessage
                name="branch"
                component="div"
                style={{ color: 'red', fontSize: '0.9em', marginTop: '4px' }}
              />
            </Box>

            <Box sx={{ marginBottom: '16px', width: '300px', margin: '5px' }}>
              <label htmlFor="batchType">Batch Type</label>
              <Field
                as="select"
                id="batchType"
                name="batchType"
                disabled={isApproved && !isLearningSuperUser && !isLearningManager}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px',
                  marginTop: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                }}
              >
                <option value="" label="Select Batch Type" />
                <option value="offline" label="Offline" />
                <option value="online" label="Online" />
              </Field>
              <ErrorMessage
                name="batchType"
                component="div"
                style={{ color: 'red', fontSize: '0.9em', marginTop: '4px' }}
              />
            </Box>
            <Box sx={{ marginBottom: '16px', width: '300px', margin: '5px' }}>
              <label htmlFor="trainingRoLocation">Training RO Location</label>
              <Field
                as="select"
                id="trainingRoLocation"
                name="trainingRoLocation"
                disabled={isApproved && !isLearningSuperUser && !isLearningManager}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px',
                  marginTop: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                }}
              >
                <option value="" label="Select Training RO Location" />
                {(trainingFormDetail?.availableRegions ?? []).map((region) => (
                  <option key={region.id} value={region.label} label={region.label} />
                ))}
              </Field>
              <ErrorMessage
                name="trainingRoLocation"
                component="div"
                style={{ color: 'red', fontSize: '0.9em', marginTop: '4px' }}
              />
            </Box>
            {values.batchType === 'offline' && (
              <>
                <Box sx={{ marginBottom: '16px', width: '300px', margin: '5px' }}>
                  <label htmlFor="address">Address</label>
                  <Field
                    type="text"
                    id="address"
                    name="address"
                    disabled={isApproved && !isLearningSuperUser && !isLearningManager}
                    placeholder="Enter Address"
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '8px',
                      marginTop: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                    }}
                  />
                  <ErrorMessage
                    name="address"
                    component="div"
                    style={{ color: 'red', fontSize: '0.9em', marginTop: '4px' }}
                  />
                </Box>

                <Box sx={{ marginBottom: '16px', width: '300px', margin: '5px' }}>
                  <label htmlFor="googleMapsLink">Google Maps Link</label>
                  <Field
                    type="url"
                    id="googleMapsLink"
                    name="googleMapsLink"
                    disabled={isApproved && !isLearningSuperUser && !isLearningManager}
                    placeholder="Enter Google Maps Link"
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '8px',
                      marginTop: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                    }}
                  />
                  <ErrorMessage
                    name="googleMapsLink"
                    component="div"
                    style={{ color: 'red', fontSize: '0.9em', marginTop: '4px' }}
                  />
                </Box>
              </>
            )}

            {/* Link for Online Training */}
            {values.batchType?.toLowerCase() == 'online' && (
              <Box sx={{ marginBottom: '16px', width: '300px', margin: '5px' }}>
                <label htmlFor="trainingLink">Link for Online Training</label>
                <Field
                  type="url"
                  id="trainingLink"
                  name="trainingLink"
                  disabled={isApproved && !isLearningSuperUser && !isLearningManager}
                  placeholder="Enter Online Training Link"
                  style={{
                    display: 'block',
                    width: '100%',
                    padding: '8px',
                    marginTop: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                  }}
                />
                <ErrorMessage
                  name="trainingLink"
                  component="div"
                  style={{ color: 'red', fontSize: '0.9em', marginTop: '4px' }}
                />
              </Box>
            )}
            <Box sx={{ marginBottom: '16px', width: '300px', margin: '5px' }}>
              <label htmlFor="description">Extra Description</label>
              <Field
                as="textarea"
                id="description"
                name="description"
                disabled={isApproved && !isLearningSuperUser && !isLearningManager}
                placeholder="Ho-Name and other details"
                style={{
                  display: 'block',
                  width: '100%',
                  height: '40px',
                  padding: '8px',
                  marginTop: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                }}
              />
              <ErrorMessage
                name="description"
                component="div"
                style={{ color: 'red', fontSize: '0.9em', marginTop: '4px' }}
              />
            </Box>

            <Box sx={{ marginBottom: '16px', width: '300px', margin: '5px' }}>
              <label htmlFor="duration">Duration</label>
              <Field
                as="select"
                id="duration"
                name="duration"
                disabled={isApproved && !isLearningSuperUser && !isLearningManager}
                style={{
                  display: 'block',
                  width: '100%',
                  padding: '8px',
                  marginTop: '8px',
                  borderRadius: '4px',
                  border: '1px solid #ccc',
                }}
                onChange={(e) => {
                  const selectedDuration = e.target.value;
                  setFieldValue('duration', selectedDuration);
                  handleDurationChange(selectedDuration, setFieldValue);
                }}
              >
                <option disabled={true} value="Select Duration" label="Select Duration" />
                <option value="1-day" label="1 Day" />
                <option value="2-days" label="2 Days" />
                <option value="3-days" label="3 Days" />
                <option value="4-days" label="4 Days" />
                <option value="5-days" label="5 Days" />
              </Field>
              <ErrorMessage
                name="duration"
                component="div"
                style={{ color: 'red', fontSize: '0.9em', marginTop: '4px' }}
              />
            </Box>

            {values?.dates?.map((day, index) => (
              <Box
                key={index}
                sx={{
                  marginBottom: '16px',
                  width: '370px',
                  margin: '5px',
                }}
              >
                <label htmlFor={`dates[${index}].date`}>{day.day}</label>
                <div style={{ display: 'flex' }}>
                  <Field
                    type="datetime-local"
                    id={`dates[${index}].date`}
                    name={`dates[${index}].date`}
                    disabled={isApproved && !isLearningSuperUser && !isLearningManager}
                    min={
                      values?.dates[index - 1]?.date
                        ? values.dates[index - 1].date
                        : new Date().toISOString().slice(0, 16)
                    }
                    style={{
                      display: 'block',
                      width: '100%',
                      padding: '10px',
                      marginTop: '8px',
                      borderRadius: '4px',
                      height: '40px',
                      border: '1px solid #aaa',
                    }}
                  />

                  <Field
                    type="time"
                    id={`datesEnd[${index}].date`}
                    name={`datesEnd[${index}].date`}
                    disabled={
                      values?.dates[index]?.date
                        ? false
                        : true || (isApproved && !isLearningSuperUser && !isLearningManager)
                    }
                    style={{
                      display: 'block',
                      width: '40%',
                      height: '40px',
                      padding: '10px',
                      marginTop: '8px',
                      borderRadius: '4px',
                      border: '1px solid #aaa',
                    }}
                  />
                </div>
                <ErrorMessage
                  name={`dates[${index}].date`}
                  component="div"
                  style={{ color: 'red', fontSize: '0.9em', marginTop: '4px' }}
                />
                <ErrorMessage
                  name={`datesEnd[${index}].date`}
                  component="div"
                  style={{ color: 'red', fontSize: '0.9em', marginTop: '4px' }}
                />
              </Box>
            ))}

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                marginBottom: '16px',
                margin: '5px',
                height: '100px',
              }}
            >
              <Button
                variant="contained"
                type="submit"
                disabled={
                  isSubmitting || (isApproved && !isLearningSuperUser && !isLearningManager)
                }
                style={{ marginRight: '8px' }}
              >
                Submit
              </Button>
            </Box>
          </Form>
        )}
      </Formik>
    </Box>
  );
};

export default BatchInfoForm;
